import React, { useCallback, useEffect, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  Image,
} from "react-native";
import InfoCard from "../components/InfoCard";
import BenefitCard from "../components/BenefitCard";
import { infoSteps, benefits } from "../constants/content";
import {
  Button,
  SIZE,
  COMMON_STYLES,
  BG_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
  Spinner,
  BottomDrawer,
  TEXT_STYLES,
  BORDER_STYLES,
} from "@dehaat/dds";
import {
  DiseaseDiagnosisScreenProps,
  ROUTES as FARMER_360_ROUTES,
} from "app/farmer360/routes/types";
import fetchPastDiagnosis from "../apis/fetchPastDiagnosis";
import { FetchPastDiagnosisResponse } from "../models/diagnosis";
import PastDiagnosis from "../components/PastDiagnosis";
import { rnNavigation } from "app/utils/appConfig";
import SelectUser from "../components/SelectUser";
import { useAppDispatch, useAppSelector } from "app/hooks/reduxHooks";
import { fetchUserDetails } from "app/farmer360/KhetEdge/apis/fetchUserDetails";
import {
  setFarmerId,
  setFarmerName,
  setFarmerPhone,
  setLocation,
  selectFarmerName,
  selectFarmerPhone,
} from "app/farmer360/store/app";
import User from "app/farmer360/KhetEdge/models/user";
import { useFocusEffect } from "@react-navigation/native";
import ImageSourceDialog from "../components/modals/ImageSourceDialog";
import useImageUpload from "../hooks/useImageUpload";
import { trackEvent } from "app/farmer360/utils/tracking";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import { LANGUAGE } from "../assests/images";
import { ROUTES } from "app/routes/types";
import { isMobileDevice } from "../constants/common";
import { useTranslation } from "react-i18next";
import getClientCode from "app/farmer360/utils/helpers/getClientCode";
import { getCookieValue } from "app/farmer360/utils/helper";
import {
  ASSUMED_USER_NAME,
  ASSUMED_USER_NUMBER,
  NAVIGATION_FROM_APP_CODE,
} from "app/farmer360/constants/common";
import { APP_CODE_TYPE } from "app/farmer360/models/auth";
import fetchOrCreateUser from "../apis/fetchOrCreateUser";
import AddCropModal from "app/farmer360/KhetEdge/components/crop/AddCropModal";

const DiseaseDiagnosisScreen = ({
  navigation,
}: DiseaseDiagnosisScreenProps) => {
  const [pastHistoryData, setPastHistoryData] =
    useState<FetchPastDiagnosisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSelectUser, SetShowSelectUser] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showCropSelector, setShowCropSelector] = useState(false);
  const [cropId, setCropId] = useState<string>("");
  const farmerphone = useAppSelector(selectFarmerPhone);
  const farmerName = useAppSelector(selectFarmerName);
  const { t, i18n } = useTranslation();
  const clientCode = getClientCode(
    getCookieValue(NAVIGATION_FROM_APP_CODE) as APP_CODE_TYPE
  );
  const clientName = getCookieValue(ASSUMED_USER_NAME);
  const clientNumber = getCookieValue(ASSUMED_USER_NUMBER);

  const dispatch = useAppDispatch();

  const { uploadFromCamera, uploadFromGallery } = useImageUpload(() => {
    rnNavigation({
      name: FARMER_360_ROUTES.START_DIAGNOSIS,
      params: { cropId: cropId },
    });
  });

  const getUserDetails = async () => {
    SetShowSelectUser(false);
    let user: User | null;
    user = await fetchUserDetails();
    if (user && user.id) {
      dispatch(setFarmerId(user.id));
      dispatch(setFarmerName(user.full_name!));
      dispatch(setFarmerPhone(user.phone_number));
    }
  };

  const handleAddFarmer = () => {
    trackEvent(CustomEventTypes.CropDocImageUserTypeSelected, {
      UserType: "other farmer",
    });
    SetShowSelectUser(false);
    rnNavigation({
      name: FARMER_360_ROUTES.ADD_FARMER,
    });
  };

  const requestLocationPermission = useCallback(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          dispatch(
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            })
          );
        },
        (error) => {
          alert("Location access denied or error");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  }, [dispatch]);

  const requestCameraPermission = useCallback(() => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        if (navigator.mediaDevices?.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          stream.getTracks().forEach((track) => track.stop());
          resolve();
        } else {
          if (isMobileDevice()) {
            alert("Give camera permission from settings");
          } else {
            alert("Camera is not supported in this browser");
          }
        }
      } catch (err) {
        alert("Camera access denied");
      }
    });
  }, [dispatch]);

  const onClickStartDiagnosisBtn = async () => {
    trackEvent(CustomEventTypes.CropDocImageStartDiagnosisClicked);
    if (clientCode === "aimsapp") {
      trackEvent(CustomEventTypes.CropDocImageUserTypeSelected, {
        UserType: "other farmer",
      });
      rnNavigation({
        name: FARMER_360_ROUTES.ADD_FARMER,
      });
    } else if (clientName && clientNumber) {
      trackEvent(CustomEventTypes.CropDocImageUserTypeSelected, {
        UserType: "other farmer",
      });
      const response = await fetchOrCreateUser(clientNumber, clientName);
      if (response) {
        dispatch(setFarmerId(response.id));
        dispatch(setFarmerName(response.full_name));
        dispatch(setFarmerPhone(response.phone_number));
      }
      setShowDialog(true);
    } else {
      SetShowSelectUser(true);
    }
  };

  useFocusEffect(
    useCallback(() => {
      const fetchDiagnosis = async () => {
        setLoading(true);
        try {
          const response = await fetchPastDiagnosis();
          setPastHistoryData(response);
        } catch (err) {
          console.error("error:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchDiagnosis();
      return () => {
        setPastHistoryData(null);
      };
    }, [])
  );

  useEffect(() => {
    requestLocationPermission();
    requestCameraPermission();
    trackEvent(CustomEventTypes.CropDocImageUserLandedOnHome);
    navigation.setOptions({
      ...(getCookieValue(NAVIGATION_FROM_APP_CODE) && {
        headerShown: false,
      }),
    });
  }, []);

  return (
    <View style={[COMMON_STYLES.flex1]}>
      <ScrollView>
        <View
          style={[
            styles.gap,
            COMMON_STYLES.ph1,
            COMMON_STYLES["pv3/2"],
            BG_STYLES.bgOlive10,
          ]}
        >
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.justifyBetween,
              COMMON_STYLES.alignItemsCenter,
            ]}
          >
            <Text style={[VARIANTS_STYLES.EMPHASIZED, TYPOGRAPHY.HEADING3]}>
              {t("how_does_it_work")}
            </Text>
            <Pressable
              style={[COMMON_STYLES["mr3/4"]]}
              onPress={() => {
                rnNavigation({
                  name: ROUTES.LANGUAGE,
                  params: { goBack: true },
                });
              }}
            >
              <View
                style={[
                  COMMON_STYLES.row,
                  COMMON_STYLES.alignItemsCenter,
                  COMMON_STYLES.b1,
                  COMMON_STYLES["br1/2"],
                  BORDER_STYLES.borderPrimary100,
                  COMMON_STYLES["ph1/2"],
                  COMMON_STYLES["pv1/4"],
                ]}
              >
                <Image
                  source={LANGUAGE}
                  style={{ width: 24, height: 24 }}
                  resizeMode="stretch"
                />
                <Text
                  style={[
                    COMMON_STYLES["ml1/4"],
                    TEXT_STYLES.textPrimary100,
                    TYPOGRAPHY.CAPTION1,
                    VARIANTS_STYLES.SEMI_EMPHASIZED,
                  ]}
                >
                  {i18n.language === "hi"
                    ? "हिंदी"
                    : i18n.language === "bn"
                    ? "বাংলা"
                    : i18n.language === "mr"
                    ? "मराठी"
                    : i18n.language === "or"
                    ? "ઓડિયા"
                    : i18n.language === "gu"
                    ? "ગુજરાતી"
                    : "English"}
                </Text>
              </View>
            </Pressable>
          </View>
          {infoSteps.map((item, index) => (
            <InfoCard key={index} {...item} />
          ))}
          {!loading && pastHistoryData && pastHistoryData.count > 0 && (
            <Button
              onClick={onClickStartDiagnosisBtn}
              text={t("start_diagnosis_now")}
              size={SIZE.large}
            />
          )}
        </View>

        {loading ? (
          <View style={[COMMON_STYLES.flex1, COMMON_STYLES.p1]}>
            <Spinner />
          </View>
        ) : pastHistoryData && pastHistoryData.count > 0 ? (
          <PastDiagnosis pastHistoryData={pastHistoryData} />
        ) : (
          <View
            style={[
              COMMON_STYLES.mv1,
              BG_STYLES.bgYellow10,
              COMMON_STYLES["pv3/2"],
              COMMON_STYLES.ph1,
              styles.gap,
            ]}
          >
            <Text style={[VARIANTS_STYLES.EMPHASIZED, TYPOGRAPHY.HEADING3]}>
              {t("why_to_use")}
            </Text>
            {benefits.map((item, index) => (
              <BenefitCard key={index} {...item} />
            ))}
          </View>
        )}
      </ScrollView>
      {!loading && (!pastHistoryData || pastHistoryData.count === 0) && (
        <View
          style={[
            COMMON_STYLES.p1,
            BG_STYLES.bgWhite,
            {
              shadowColor: "#000",
              shadowOffset: { width: 0, height: -3 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 5,
            },
          ]}
        >
          <Button
            onClick={onClickStartDiagnosisBtn}
            text={t("start_diagnosis_now")}
            size={SIZE.large}
          />
        </View>
      )}
      {showSelectUser && (
        <BottomDrawer
          onClose={() => SetShowSelectUser(false)}
          visible={showSelectUser}
          heading={t("who_are_you_diagnosing_for")}
          closeOnBackdropPress
        >
          <SelectUser
            handleUpload={() => {
              getUserDetails().then(() => {
                trackEvent(CustomEventTypes.CropDocImageUserTypeSelected, {
                  UserType: "self",
                });
                trackEvent(CustomEventTypes.CropDocImageFarmerAdded, {
                  UserType: "self",
                  farmername: farmerName,
                  farmermobile: farmerphone,
                });
              });

              SetShowSelectUser(false);
              setShowCropSelector(true);
            }}
            handleAddFarmer={handleAddFarmer}
          />
        </BottomDrawer>
      )}

      {showCropSelector && (
        <AddCropModal
          onSubmit={(crop) => {
            if (crop?.id) {
              setCropId(crop.id);
              setShowCropSelector(false);
              setShowDialog(true);
            }
          }}
          onClose={() => setShowCropSelector(false)}
        />
      )}

      {showDialog && (
        <ImageSourceDialog
          visible={showDialog}
          onClose={() => setShowDialog(false)}
          uploadFromCamera={() => {
            trackEvent(CustomEventTypes.CropDocImageSourceSelected, {
              selectiontype: "camera",
            });
            setShowDialog(false);
            uploadFromCamera();
          }}
          uploadFromGallery={() => {
            trackEvent(CustomEventTypes.CropDocImageSourceSelected, {
              selectiontype: "gallery",
            });
            setShowDialog(false);
            uploadFromGallery();
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  gap: {
    gap: 16,
  },
});

export default DiseaseDiagnosisScreen;
